import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Search, Mail, Phone, Users, CalendarClock, Quote } from "lucide-react";
import { getAllBookings, getQuoteRequests, getAllGlamPackages } from "../../../services/api";
import { deriveCustomers, Customer } from "../../../lib/customers";
import { formatSlotDate } from "../../../lib/timezone";
import { cn } from "../../../lib/util";
import { ToastProvider } from "../../../components/providers/toast-provider";

export default function CustomersPage() {
  const [params, setParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [packages, setPackages] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const selectedKey = params.get("c");

  useEffect(() => {
    let active = true;
    Promise.all([getAllBookings(), getQuoteRequests(), getAllGlamPackages()])
      .then(([b, q, p]: any) => {
        if (!active) return;
        setCustomers(deriveCustomers(b?.data?.data ?? [], q?.data?.data ?? []));
        setPackages(p?.data?.data ?? []);
      })
      .catch(() => toast.error("Could not load customers."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.name, c.email, c.phone].some((f) => f.toLowerCase().includes(q))
    );
  }, [customers, query]);

  const selected = useMemo(
    () => customers.find((c) => c.key === selectedKey) ?? filtered[0] ?? null,
    [customers, filtered, selectedKey]
  );

  const packageTitle = (id?: string) => packages.find((p) => p.id === id)?.title ?? "Session";
  const select = (key: string) => setParams({ c: key }, { replace: true });

  return (
    <div className="mx-auto max-w-6xl">
      <ToastProvider />
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Operations</p>
        <h1 className="font-serif text-3xl text-[#3a2e2c]">Customers</h1>
        <p className="mt-1 text-sm text-[#8a7a78]">
          Everyone who's booked or requested a quote, gathered from your bookings.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 py-20 text-[#9A6B69]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
          Loading customers…
        </div>
      ) : customers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E3C9C8] bg-white px-6 py-16 text-center">
          <Users className="mx-auto h-10 w-10 text-[#D9C4C3]" />
          <p className="mt-3 font-serif text-xl text-[#3a2e2c]">No customers yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-[#8a7a78]">
            As clients book sessions or request quotes, they'll appear here with their
            full history.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-5">
          {/* Directory */}
          <div className="lg:col-span-2">
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9A6B69]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or phone"
                className="h-11 w-full rounded-full border border-[#EAD7D6] bg-white pl-10 pr-4 text-sm text-[#3a2e2c] placeholder:text-[#9A6B69] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
              />
            </div>
            <p className="mb-2 px-1 text-xs text-[#9A6B69]">
              {filtered.length} customer{filtered.length === 1 ? "" : "s"}
            </p>
            <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto pr-1">
              {filtered.map((c) => {
                const active = selected?.key === c.key;
                return (
                  <li key={c.key}>
                    <button
                      type="button"
                      onClick={() => select(c.key)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                        active
                          ? "border-[#B87775] bg-[#FBEFEF]/60"
                          : "border-[#EAD7D6] bg-white hover:border-[#B87775]"
                      )}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#B87775] text-sm font-medium text-white">
                        {c.name.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-[#3a2e2c]">{c.name}</span>
                        <span className="block truncate text-xs text-[#9A6B69]">
                          {c.email || c.phone || "—"}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-[#9A6B69]">
                        {c.bookingsCount + c.quotesCount}×
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Profile */}
          <div className="lg:col-span-3">
            {selected ? (
              <div className="rounded-2xl border border-[#EAD7D6] bg-white p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#B87775] text-lg font-medium text-white">
                    {selected.name.slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-serif text-2xl text-[#3a2e2c]">{selected.name}</h2>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#8a7a78]">
                      {selected.email && (
                        <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 hover:text-[#B87775]">
                          <Mail className="h-3.5 w-3.5" /> {selected.email}
                        </a>
                      )}
                      {selected.phone && (
                        <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#B87775]">
                          <Phone className="h-3.5 w-3.5" /> {selected.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3">
                  <MiniStat label="Bookings" value={selected.bookingsCount} />
                  <MiniStat label="Quotes" value={selected.quotesCount} />
                  <MiniStat
                    label="Last activity"
                    value={selected.lastActivity ? formatSlotDate(selected.lastActivity) : "—"}
                    small
                  />
                </div>

                <h3 className="mb-2 mt-6 text-xs uppercase tracking-[0.2em] text-[#B87775]">History</h3>
                <ul className="divide-y divide-[#F0E2E1]">
                  {selected.history.map((h, i) => (
                    <li key={i} className="flex items-center gap-3 py-3">
                      <span
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                          h.kind === "booking" ? "bg-[#FBEFEF] text-[#B87775]" : "bg-[#F0E2E1] text-[#8a5a58]"
                        )}
                      >
                        {h.kind === "booking" ? (
                          <CalendarClock className="h-4 w-4" />
                        ) : (
                          <Quote className="h-4 w-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-[#3a2e2c]">
                          {packageTitle(h.glam_package_id)}
                          <span className="ml-2 text-xs uppercase tracking-wide text-[#9A6B69]">
                            {h.kind}
                          </span>
                        </p>
                        <p className="text-xs tabular-nums text-[#9A6B69]">
                          {h.date ? formatSlotDate(h.date) : "Date not set"}
                        </p>
                      </div>
                      {h.kind === "booking" && h.id && (
                        <Link
                          to={`/control/bookings/view/${h.id}`}
                          className="shrink-0 text-sm text-[#B87775] hover:underline"
                        >
                          View
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-[#E3C9C8] bg-white px-6 py-16 text-center text-sm text-[#8a7a78]">
                Select a customer to see their history.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, small }: { label: string; value: string | number; small?: boolean }) {
  return (
    <div className="rounded-xl border border-[#EAD7D6] bg-[#FFFBFB] p-3">
      <p className={cn("font-serif tabular-nums text-[#3a2e2c]", small ? "text-base" : "text-2xl")}>
        {value}
      </p>
      <p className="mt-0.5 text-xs text-[#9A6B69]">{label}</p>
    </div>
  );
}
